import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import FrontendLayout from "../../components/frontend/FrontendLayout";
import FormatIndianPrice from "../../components/frontend/FormatIndianPrice";
import { formatShortPrice } from "../../utils/formatNumber";
import { formatNumberIndian } from "../../utils/formatNumber";
import { handleApiError } from "../../utils/apiHelpers";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Add this utility function at the top of your file, after the imports
const trackLandViewInSession = (landId: number): boolean => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    // Get today's views from sessionStorage
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");

    // Check if user already viewed this land today
    if (todayViews[landId]) {
      return false; // Already viewed today
    }

    // Mark this land as viewed today
    todayViews[landId] = true;
    sessionStorage.setItem(sessionKey, JSON.stringify(todayViews));

    return true; // New view for today
  } catch (error) {
    console.error("Error tracking land view in session:", error);
    return false;
  }
};

const getSessionViews = (currentLand: LandDetail | null): LandDetail[] => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");
    const viewedLandIds = Object.keys(todayViews).map(Number);

    // Return empty array if no session views or if land data isn't loaded yet
    if (viewedLandIds.length === 0 || !currentLand) return [];

    // For simplicity, we'll just return the current currentLand if it's in session
    // In a real implementation, you might want to store more data in session
    if (todayViews[currentLand.ln_id]) {
      return [currentLand]; // Return current land as a session view
    }

    return [];
  } catch (error) {
    console.error("Error reading session views:", error);
    return [];
  }
};

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface LandDetail {
  ln_id: number;
  ln_landno: string;
  ln_title: string;
  ln_price: number;
  ln_total_area_sqft: number;
  ln_total_sqft_area: string;
  ln_seo_url: string;
  ln_post_date: string;
  ln_plottype: string;
  ln_anyconstruction: string;
  ln_legal_status: string;
  ln_infrastructural_status: string;
  ln_boundarywall: string;
  ln_overlooking: string;
  ln_transaction_type: string;
  ln_locality: string;
  ln_address: string;
  ln_width_roadfacing: number;
  ln_desc: string;
  ln_hilights: string;
  ln_possesion_status: string;
  ln_map_long: string;
  ln_map_lat: string;
  ln_seller_type: string;
  ln_zone: string;
  ln_growth: string;
  ln_gated: string;
  ln_loan: string;
  ln_land_avail: string;
  ln_classification: string;
  ln_facing: string;
  ln_corner: string;
  ln_fencing_type: string;
  ln_electric_supply: string;
  ln_water_supply: string;
  ln_nearby_landmark: string;
  ln_topography: string;
  ln_water_looging: string;
  ln_rerano: string;
  ln_authority_approve: string;
  ln_encumbrance_status: string;
  ln_mutation: string;
  ln_patta_avail: string;
  ln_sitevisit_avail: string;
  ln_sitevisit_link: string;
  ln_soiltype: string;
  ln_featured: string;
  ln_dimension_east: string;
  ln_dimension_west: string;
  ln_dimension_north: string;
  ln_dimension_south: string;
  ln_allowed_floors: number;
  ln_image: string[];
  ln_video: string[];
  ln_encumb_cert: string[];
  ln_drone_vid: string[];
  ln_status: number;
  ln_admin_approve: number;
  us_name: string;
  us_email: string;
  us_mobile: string;
  us_profileimage: string;
  us_usertype: number;
}

const PublicLandView: React.FC = () => {
  const [land, setLand] = useState<LandDetail | null>(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [similarLands, setSimilarLands] = useState<LandDetail[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<LandDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasEnquiry, setHasEnquiry] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState("sqft");
  const [convertedArea, setConvertedArea] = useState(0);

  const [showModal, setShowModal] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const userId = localStorage.getItem("frontendUserId");
  const token = localStorage.getItem("frontendUserToken");
  const isLoggedIn = !!localStorage.getItem("frontendUserId");
  const loggedInUserId = localStorage.getItem("frontendUserId");
  const loggedInUserName = localStorage.getItem("frontendUserName") || "";
  const loggedInUserEmail = localStorage.getItem("frontendUserEmail") || "";
  const loggedInUserMobile = localStorage.getItem("frontendUserMobile") || "";
  const [enquiryForm, setEnquiryForm] = useState({
    userType: "Buyer",
    name: loggedInUserId ? loggedInUserName : "",
    email: loggedInUserId ? loggedInUserEmail : "",
    mobile: loggedInUserId ? loggedInUserMobile : "",
    message: "",
  });

  // OTP Modal State
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [registeredUserId, setRegisteredUserId] = useState<number | null>(null);
  const [pendingEnquiry, setPendingEnquiry] = useState<any>(null);

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEnquiryForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle radio button change
  const handleUserTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnquiryForm((prev) => ({ ...prev, userType: e.target.value }));
  };

  const handleOtpDigitChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // Only allow single digit
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value;
    setOtpDigits(newOtpDigits);
    setOtpError("");

    // Auto focus next box if digit entered
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-digit-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // If all filled, update otp
    setOtp(newOtpDigits.join(""));
  };

  // Handle OTP submission
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setOtpError("OTP is required.");
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("OTP must be 6 digits.");
      return;
    }

    if (registeredUserId === null) {
      toast.error("User ID not found for OTP verification. Please try again.");
      setShowOtpModal(false);
      return;
    }

    setOtpLoading(true);
    try {
      // Verify OTP
      const verifyOtpPayload = {
        userId: registeredUserId,
        otp: parseInt(otp, 10),
      };
      const otpRes = await axios.post(
        `${API_BASE}/api/enquiry/verify-otp`,
        verifyOtpPayload
      );

      if (otpRes.data.success) {
        toast.success("OTP verified successfully!");

        // Perform auto-login
        const loginPayload = {
          email: enquiryForm.email,
          password: `${enquiryForm.name.replace(/\s+/g, "")}@123`, // Password format as per backend (name without spaces + @123)
          us_usertype: 3, // Buyer
        };

        const loginRes = await axios.post(
          `${API_BASE}/api/authuser/login`,
          loginPayload
        );

        if (loginRes.data.success) {
          // Store user data in localStorage
          localStorage.setItem("frontendUserToken", loginRes.data.token);
          localStorage.setItem("frontendUserId", loginRes.data.profile.id);
          localStorage.setItem(
            "frontendUserType",
            loginRes.data.profile.usertype
          );
          localStorage.setItem("frontendUserName", loginRes.data.profile.name);
          localStorage.setItem(
            "frontendUserEmail",
            loginRes.data.profile.email
          );
          localStorage.setItem(
            "frontendUserMobile",
            loginRes.data.profile.mobile
          );
          localStorage.setItem(
            "frontendUserIp",
            loginRes.data.profile.clientIp
          );

          // Dispatch login event
          window.dispatchEvent(new Event("loginStatusChanged"));

          // Update logged-in state
          setEnquiryForm((prev) => ({
            ...prev,
            name: loginRes.data.profile.name,
            email: loginRes.data.profile.email,
            mobile: loginRes.data.profile.mobile,
          }));

          // Submit pending enquiry if exists
          if (pendingEnquiry) {
            const enquiryPayload = {
              ...pendingEnquiry,
              eq_userid: Number(loginRes.data.profile.id), // Update user ID
            };

            const enquiryRes = await axios.post(
              `${API_BASE}/api/enquiry/add`,
              enquiryPayload
            );

            if (enquiryRes.data.success) {
              toast.success("Enquiry submitted successfully!");
              setHasEnquiry(true);
              setEnquirySubmitted(true);
              // Clear pending enquiry
              setPendingEnquiry(null);
              localStorage.removeItem("pendingEnquiry");
            } else {
              toast.error(
                enquiryRes.data.message || "Failed to submit enquiry."
              );
              if (enquiryRes.data.message?.includes("already submitted")) {
                setHasEnquiry(true);
              }
            }
          }

          // Clear OTP-related states
          setShowOtpModal(false);
          setOtp("");
          setOtpDigits(["", "", "", "", "", ""]);
          setRegisteredUserId(null);
          // toast.info("You are now logged in!");
        } else {
          toast.error(
            loginRes.data.message ||
              "Auto-login failed. Please try logging in manually."
          );
          setTimeout(() => {
            navigate("/authuser/login");
          }, 3000);
        }
      } else {
        toast.error(
          otpRes.data.message || "OTP verification failed. Please try again."
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "An unexpected error occurred during OTP verification or auto-login.";
      toast.error(errorMessage);
      console.error("Error during OTP verification or auto-login:", error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");

    try {
      const loginRes = await axios.post(`${API_BASE}/api/authuser/login`, {
        email: loginForm.email,
        password: loginForm.password,
        us_usertype: 3, // Buyer
      });

      if (loginRes.data.success) {
        // Store user data in localStorage
        localStorage.setItem("frontendUserToken", loginRes.data.token);
        localStorage.setItem("frontendUserId", loginRes.data.profile.id);
        localStorage.setItem(
          "frontendUserType",
          loginRes.data.profile.usertype
        );
        localStorage.setItem("frontendUserName", loginRes.data.profile.name);
        localStorage.setItem("frontendUserEmail", loginRes.data.profile.email);
        localStorage.setItem(
          "frontendUserMobile",
          loginRes.data.profile.mobile
        );
        localStorage.setItem("frontendUserIp", loginRes.data.profile.clientIp);

        // Dispatch login event
        window.dispatchEvent(new Event("loginStatusChanged"));

        // Update enquiry form with user data
        setEnquiryForm({
          ...enquiryForm,
          name: loginRes.data.profile.name,
          email: loginRes.data.profile.email,
          mobile: loginRes.data.profile.mobile,
        });

        // Submit the enquiry automatically after successful login
        if (land) {
          const enquiryPayload = {
            eq_usertype: enquiryForm.userType,
            eq_name: loginRes.data.profile.name,
            eq_email: loginRes.data.profile.email,
            eq_mobile: loginRes.data.profile.mobile,
            eq_message: enquiryForm.message,
            eq_landid: land.ln_id,
            eq_userid: Number(loginRes.data.profile.id),
            eq_status: 1,
          };

          const enquiryRes = await axios.post(
            `${API_BASE}/api/enquiry/add`,
            enquiryPayload
          );

          if (enquiryRes.data.success) {
            toast.success("Enquiry submitted successfully!");
            setHasEnquiry(true);
          } else {
            toast.error(enquiryRes.data.message || "Failed to send enquiry.");
            if (enquiryRes.data.message?.includes("already submitted")) {
              setHasEnquiry(true);
            }
          }
        }

        // Close the modal
        setShowLoginModal(false);
        // toast.success("Login successful!");
      } else {
        setLoginError(
          loginRes.data.message || "Login failed. Please try again."
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";
      setLoginError(errorMessage);
      console.error("Login error:", error);
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle form submission
  const handleEnquirySubmit = async (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();

    if (!land) {
      toast.error("Invalid land data.");
      return;
    }

    // Basic validation
    if (
      !enquiryForm.name ||
      !enquiryForm.email ||
      !enquiryForm.mobile ||
      !enquiryForm.userType ||
      !enquiryForm.message
    ) {
      toast.error(
        "Please fill all fields (Name, Email, Mobile, Message) for a quick enquiry."
      );
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enquiryForm.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    // Mobile validation
    if (!/^\d{10}$/.test(enquiryForm.mobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    // Prepare enquiry payload
    const enquiryPayload = {
      eq_usertype: enquiryForm.userType,
      eq_name: enquiryForm.name,
      eq_email: enquiryForm.email,
      eq_mobile: enquiryForm.mobile,
      eq_message: enquiryForm.message,
      eq_landid: land.ln_id,
      eq_userid: isLoggedIn ? Number(loggedInUserId) : 0,
      eq_status: 1,
    };

    if (!isLoggedIn) {
      try {
        // Check if email is registered
        const res = await axios.post(`${API_BASE}/api/authuser/check-email`, {
          email: enquiryForm.email,
          usertype: 3, // Buyer
        });

        if (res.data.exists) {
          if (!res.data.isVerified) {
            // Email exists but not verified, resend OTP
            const registerRes = await axios.post(
              `${API_BASE}/api/authuser/register`,
              {
                us_name: enquiryForm.name,
                us_email: enquiryForm.email,
                us_password: `${enquiryForm.name.replace(/\s+/g, "")}@123`, // Match backend password format
                us_mobile: enquiryForm.mobile,
                us_usertype: 3, // Buyer
              }
            );

            if (registerRes.data.success) {
              setRegisteredUserId(registerRes.data.userId);
              setPendingEnquiry(enquiryPayload);
              setShowOtpModal(true);
              toast.info(
                "Please verify your email with the OTP sent to your inbox."
              );
            } else {
              toast.error(
                registerRes.data.message ||
                  "Failed to initiate OTP verification."
              );
            }
          } else {
            // Email is registered and verified, show login modal
            setPendingEnquiry(enquiryPayload);
            setLoginForm({
              email: enquiryForm.email,
              password: "", // Let user enter password
            });
            setShowLoginModal(true);
            toast.info("Please login to send your enquiry.");
          }
        } else {
          // Email not registered, register and send OTP
          const registerRes = await axios.post(
            `${API_BASE}/api/authuser/register`,
            {
              us_name: enquiryForm.name,
              us_email: enquiryForm.email,
              us_password: `${enquiryForm.name.replace(/\s+/g, "")}@123`, // Match backend password format
              us_mobile: enquiryForm.mobile,
              us_usertype: 3, // Buyer
            }
          );

          if (registerRes.data.success) {
            setRegisteredUserId(registerRes.data.userId);
            setPendingEnquiry(enquiryPayload);
            setShowOtpModal(true);
            toast.info(
              "Please verify your email with the OTP sent to your inbox."
            );
          } else {
            toast.error(registerRes.data.message || "Failed to register user.");
          }
        }
      } catch (err) {
        console.error("Error checking email or registering:", err);
        toast.error("Failed to process enquiry. Please try again.");
      }
      return;
    }

    // User is logged in, submit enquiry directly
    try {
      const res = await axios.post(
        `${API_BASE}/api/enquiry/add`,
        enquiryPayload
      );
      if (res.data.success) {
        toast.success("Enquiry submitted successfully!");
        setHasEnquiry(true);
        setEnquiryForm({
          userType: "Buyer",
          name: isLoggedIn ? loggedInUserName : "",
          email: isLoggedIn ? loggedInUserEmail : "",
          mobile: isLoggedIn ? loggedInUserMobile : "",
          message: "",
        });
      } else {
        toast.error(res.data.message || "Failed to send enquiry.");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.status === 400
          ? err.response.data.message ||
            "You have already submitted an enquiry for this land."
          : "Failed to send enquiry.";
      toast.error(errorMessage);
      if (
        err.response?.status === 400 &&
        err.response.data.message.includes("already submitted")
      ) {
        setHasEnquiry(true);
      }
      console.error("Enquiry submission error:", err);
    }
  };

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch enquiry status
  const checkEnquiryStatus = async () => {
    try {
      const userId = Number(loggedInUserId) || 0;
      if (!userId || !land) return;

      const res = await axios.get(`${API_BASE}/api/enquiry/check/${userId}`, {
        params: { landid: land.ln_id },
      });

      if (res.data.success) {
        setHasEnquiry(res.data.hasEnquiry);
      } else {
        console.warn("Failed to check enquiry status:", res.data.message);
      }
    } catch (err) {
      console.error("Error checking enquiry status:", err);
    }
  };

  const handleWishlistClick = async (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();

    const userId = Number(localStorage.getItem("frontendUserId")) || 0;

    if (!userId) {
      toast.info("Please login to save your activities.");
      setTimeout(() => {
        navigate("/authuser/login");
      }, 1000);
      return;
    }

    if (!land) {
      toast.dismiss();
      toast.error("Invalid land data.");
      return;
    }

    try {
      const payload = {
        wl_landid: land.ln_id,
        wl_userid: userId,
      };

      const res = await axios.post(`${API_BASE}/api/wishlist/toggle`, payload);

      if (res.data.success) {
        setIsWishlisted(res.data.isWishlisted);
        toast.dismiss();
        toast.success(res.data.message);
      } else {
        toast.dismiss();
        toast.error(res.data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Wishlist error:", err);
      toast.dismiss();
      toast.error("Failed to update wishlist.");
    }
  };

  const storeLandView = async (landId: number) => {
    try {
      const userId = Number(localStorage.getItem("frontendUserId")) || 0;

      const isNewViewToday = trackLandViewInSession(landId);

      if (!isNewViewToday) {
        console.log(
          "User already viewed this land today - not counting as new view"
        );
        return;
      }

      const payload = {
        landid: landId,
        userid: userId,
      };

      await axios.post(`${API_BASE}/api/landdata/storeview`, payload);
      console.log("Land view stored successfully");

      if (land) {
        setRecentlyViewed((prev) => {
          // Remove if already exists to avoid duplicates
          const filtered = prev.filter((item) => item.ln_id !== land.ln_id);
          // Add current land at the beginning
          return [land, ...filtered];
        });
      }
    } catch (err) {
      console.error("Error storing land view:", err);
    }
  };

  const fetchLandDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/landadd/edit/${id}`);

      if (res.data.success) {
        const landData = res.data.landadd;
        if (
          landData.ln_status === 1 &&
          landData.ln_admin_approve === 1 &&
          landData.ln_land_avail === "Avail"
        ) {
          setLand(landData);
          storeLandView(landData.ln_id);
        } else {
          navigate("/403");
        }
      } else {
        toast.error("Land not found");
        navigate("/");
      }
    } catch (err) {
      console.error("Error fetching land details:", err);
      toast.error("Failed to load land details");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchSimilarLands = async () => {
    try {
      if (land) {
        const res = await axios.get(
          `${API_BASE}/api/similarplots/list/${land.ln_id}`
        );
        if (res.data.success) {
          const filteredSimilarLands = res.data.similarplots.filter(
            (plot: any) =>
              plot.ln_admin_approve === 1 &&
              plot.ln_status === 1 &&
              plot.ln_land_avail === "Avail"
          );
          setSimilarLands(filteredSimilarLands);
        }
      }
    } catch (err) {
      console.error("Error fetching similar lands:", err);
    }
  };

  const fetchRecentlyViewed = async () => {
    try {
      let combinedViews: LandDetail[] = [];

      // 1. Get session views (today's views)
      const sessionViews = getSessionViews(land);

      // 2. Get database views (only if user is logged in)
      if (userId && token) {
        const res = await axios.get(
          `${API_BASE}/api/landdata/recentviews/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (res.data.success) {
          const filteredRecentViews = res.data.recentViews.filter(
            (item: any) =>
              item.ln_admin_approve === 1 &&
              item.ln_status === 1 &&
              item.ln_land_avail === "Avail"
          );

          const formatted = filteredRecentViews.map((item: any) => ({
            ln_id: item.land_id,
            ln_title: item.ln_title,
            ln_locality: item.ln_locality,
            ln_price: item.ln_price,
            ln_plottype: item.ln_plottype,
            ln_total_area_sqft: item.ln_total_area_sqft,
            ln_seo_url: item.ln_seo_url,
            ln_image: item.image_files,
            // Add other necessary fields if needed
            // ln_admin_approve: 1,
            // ln_status: 1,
            // ln_land_avail: "Avail",
          }));

          combinedViews = [...sessionViews, ...formatted];
        }
      } else {
        // If not logged in, only show session views
        combinedViews = sessionViews;
      }

      // Remove duplicates (in case same land appears in both sources)
      const uniqueViews = combinedViews.filter(
        (view, index, self) =>
          index === self.findIndex((v) => v.ln_id === view.ln_id)
      );

      setRecentlyViewed(uniqueViews);
    } catch (err) {
      console.error("Error fetching recently viewed lands:", err);
      // Fallback to just session views if API fails
      setRecentlyViewed(getSessionViews(land));
    }
  };

  const conversionFactors: Record<string, number> = {
    sqft: 1,
    sqyard: 1 / 9,
    sqm: 0.092903,
    grounds: 0.0020661157,
    aankadam: 0.0025,
    rood: 0.0000229568,
    chataks: 0.033057851,
    perch: 0.000229568,
    guntha: 0.000229568,
    ares: 0.000092903,
    biswa: 0.000114784,
    acre: 0.0000229568,
    bigha: 0.000151873,
    kottah: 0.000057872,
    hectare: 0.0000092903,
    marla: 0.000025,
    kanal: 0.000005,
    cent: 0.00229568,
  };

  useEffect(() => {
    if (land) {
      const sqft = Number(land.ln_total_sqft_area);
      const factor = conversionFactors[selectedUnit];
      setConvertedArea(sqft * factor);
    }
  }, [land, selectedUnit]);

  const handleShareClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: "Check out this land plot!",
          url: shareUrl,
        });
      } catch (err) {
        console.error("Share cancelled", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied to clipboard!");
      } catch (err) {
        console.error("Copy failed:", err);
        alert("Failed to copy link");
      }
    }
  };

  const contactSectionRef = useRef<HTMLDivElement | null>(null);

  const scrollToContact = () => {
    contactSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkWishlistStatus = async () => {
    try {
      const userId = Number(localStorage.getItem("frontendUserId")) || 0;
      if (!userId || !land) return;

      const res = await axios.get(`${API_BASE}/api/wishlist/list/${userId}`);
      if (res.data.success) {
        const found = res.data.wishlist.some(
          (item: any) => item.wl_landid === land.ln_id
        );
        setIsWishlisted(found);
      }
    } catch (err) {
      console.error("Error checking wishlist:", err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchLandDetails();
      fetchRecentlyViewed();
    }
  }, [id]);

  useEffect(() => {
    if (land) {
      fetchSimilarLands();
      checkWishlistStatus();
      checkEnquiryStatus();
      setRecentlyViewed((prev) => {
        if (!land) return prev;

        // Remove if already exists to avoid duplicates
        const filtered = prev.filter((item) => item.ln_id !== land.ln_id);
        // Add current land at the beginning
        return [land, ...filtered];
      });
    }
  }, [land]);

  // Check for pending enquiry after login

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getAllMediaItems = () => {
    const mediaItems: any[] = [];

    if (land?.ln_image && land.ln_image.length > 0) {
      land.ln_image.forEach((image, index) => {
        mediaItems.push({
          type: "image",
          src: `${API_BASE}/Uploads/lands/${image}`,
          thumb: `${API_BASE}/Uploads/lands/${image}`,
          key: `image-${index}`,
        });
      });
    } else {
      mediaItems.push({
        type: "image",
        src: `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`,
        thumb: `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`,
        key: `image-placeholder`,
      });
    }

    if (land?.ln_video && land.ln_video.length > 0) {
      land.ln_video.forEach((video, index) => {
        mediaItems.push({
          type: "video",
          src: `${API_BASE}/Uploads/lands/${video}`,
          thumb: `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/video-play-btn-featured.png`,
          key: `video-${index}`,
        });
      });
    }

    if (land?.ln_drone_vid && land.ln_drone_vid.length > 0) {
      land.ln_drone_vid.forEach((droneVideo, index) => {
        mediaItems.push({
          type: "drone",
          src: `${API_BASE}/Uploads/lands/${droneVideo}`,
          thumb: `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/video-play-btn-featured.png`,
          key: `drone-${index}`,
        });
      });
    }

    return mediaItems;
  };

  const nextSlide = () => {
    const mediaItems = getAllMediaItems();
    setCurrentSlide((prev) => (prev + 1) % mediaItems.length);
  };

  const prevSlide = () => {
    const mediaItems = getAllMediaItems();
    setCurrentSlide(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length
    );
  };

  const maskPhone = (phone: string) => {
    if (!phone) return "";
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 6) return phone;
    const firstPart = clean.slice(0, 3);
    const middlePart = clean.slice(3, 6);
    return `${firstPart}***${middlePart}**`;
  };

  const maskEmail = (email: string) => {
    if (!email) return "";
    const [user, domain] = email.split("@");
    if (!domain) return email;

    let visiblePart = user.length > 2 ? user.slice(0, 2) : user.slice(0, 1);
    let maskedPart = "*".repeat(user.length - visiblePart.length);
    return `${visiblePart}${maskedPart}@${domain}`;
  };

  // Handle cloning for carousel
  useEffect(() => {
    const carousel = document.querySelector("#multiCarouselSimilarLands");
    if (carousel) {
      const items = carousel.querySelectorAll(".carousel-item");
      items.forEach((el) => {
        const minPerSlide = 3;
        let next = el.nextElementSibling;
        for (let i = 1; i < minPerSlide; i++) {
          if (!next) next = items[0];
          const cloneChild = next.querySelector("li")?.cloneNode(true);
          if (cloneChild) {
            el.querySelector("ul")?.appendChild(cloneChild);
          }
          next = next.nextElementSibling;
        }
      });
    }
  }, [similarLands]);

  if (loading) {
    return (
      <FrontendLayout>
        <div className="tf-container py-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </FrontendLayout>
    );
  }

  if (!land) {
    return (
      <FrontendLayout>
        <div className="tf-container py-5">
          <div className="text-center">
            <h4>Land not found</h4>
            <button className="btn btn-primary" onClick={() => navigate("/")}>
              Back to Home
            </button>
          </div>
        </div>
      </FrontendLayout>
    );
  }

  const mediaItems = getAllMediaItems();

  return (
    <FrontendLayout>
      <ToastContainer />
      <button
        className="tf-btn tf-btn-export contact-float style-border me-2 px-5 glow-blink"
        onClick={scrollToContact}
      >
        <i className="bi bi-telephone-fill"></i>
        Contact {land.us_usertype === 1 ? "Owner" : "Agent"}
      </button>
      {/* Breadcrumb Section */}
      <section className="flat-title style-2 mb-4">
        <div className="tf-container">
          <div className="row">
            <div className="col-lg-12">
              <div className="title-inner mb-0">
                <ul className="breadcrumb">
                  <li>
                    <Link className="home fw-6 text-color-3" to="/">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link className="home fw-6 text-color-3" to="/lands">
                      Land Listing
                    </Link>
                  </li>
                  <li>Land View</li>
                  <li>ID: {land.ln_landno}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="tf-container py-5">
        <div className="row">
          {/* Left Column - Gallery */}
          <div className="col-lg-5 pb-5">
            {/* Main Slide */}
            <div className="slider-main" id="mainImageWrapper">
              <button className="nav-btn left" onClick={prevSlide}>
                &#10094;
              </button>
              <div id="mainSlide">
                {mediaItems.length > 0 && (
                  <>
                    {mediaItems[currentSlide].type === "image" ? (
                      <img
                        src={mediaItems[currentSlide].src}
                        alt={`Land media ${currentSlide + 1}`}
                        style={{
                          width: "100%",
                          height: "400px",
                          objectFit: "cover",
                        }}
                        onClick={() => setShowModal(true)} // Add click to open modal
                      />
                    ) : (
                      <video
                        width="100%"
                        height="400"
                        controls
                        style={{ objectFit: "cover" }}
                      >
                        <source
                          src={mediaItems[currentSlide].src}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </>
                )}
              </div>
              <button className="nav-btn right" onClick={nextSlide}>
                &#10095;
              </button>
            </div>

            {/* Thumbnails */}
            <div className="thumbnails-wrapper">
              <div className="thumbnails">
                {mediaItems.map((item, index) => (
                  <img
                    key={item.key}
                    src={item.thumb}
                    className={currentSlide === index ? "active" : ""}
                    onClick={() => setCurrentSlide(index)}
                    alt={`Thumbnail ${index + 1}`}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </div>
            </div>

            {/* Modal for image viewing */}
            {showModal && (
              <div
                className="modal"
                style={{
                  position: "fixed",
                  zIndex: 1000,
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0,0,0,0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  className="close"
                  onClick={() => setShowModal(false)}
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "35px",
                    color: "#f1f1f1",
                    fontSize: "40px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  &times;
                </span>
                <div
                  style={{
                    margin: "auto",
                    display: "block",
                    width: "80%",
                    maxWidth: "700px",
                    textAlign: "center",
                  }}
                >
                  {mediaItems[currentSlide].type === "image" ? (
                    <img
                      src={mediaItems[currentSlide].src}
                      alt={`Land media ${currentSlide + 1}`}
                      style={{
                        width: "100%",
                        maxHeight: "80vh",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <video
                      width="100%"
                      height="auto"
                      controls
                      autoPlay
                      style={{ maxHeight: "80vh" }}
                    >
                      <source
                        src={mediaItems[currentSlide].src}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div className="col-lg-7 pb-5">
            <div className="box-overview">
              <div className="heading flex justify-between pb-3 mb-4">
                <div className="title text-8 fw-6 text-color-heading">
                  {land.ln_title}
                </div>
                <div className="price text-8 fw-6 text-color-heading">
                  <i className="bi bi-currency-rupee me-1"></i>
                  {formatShortPrice(land.ln_price)}
                  <span className="h6 lh-30 fw-4 text-color-default ms-2">
                    <i className="bi bi-currency-rupee me-1"></i>
                    {formatNumberIndian(land.ln_total_area_sqft)}/sqft
                  </span>
                </div>
              </div>

              <div className="info flex justify-between">
                <div className="feature">
                  <h6 className="location text-1 flex items-center gap-10">
                    <i className="icon-location"></i>
                    {land.ln_locality}, {land.ln_address.split(",")[0]}
                  </h6>
                  <ul className="meta-list flex">
                    <li className="text-1 flex">
                      Posted on {formatDate(land.ln_post_date)}
                    </li>
                  </ul>
                </div>
                <div className="action action-button-list">
                  
                </div>
                <div className="action action-button-list">
                  <ul className="list-action">
                    <p className="pt-3">
                        ID: <strong>{land.ln_landno}</strong>
                    </p>
                    <li>
                      <a href="#" onClick={handleWishlistClick}>
                        <i
                          className={`bi ${
                            isWishlisted
                              ? "bi-heart-fill text-danger"
                              : "bi-heart"
                          }`}
                        ></i>
                      </a>
                    </li>

                    <li>
                      <a href="#" onClick={handleShareClick}>
                        <i className="bi bi-share"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Property Features Grid */}
              <div className="row">
                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec1">
                      <i className="fa fa-expand"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">Plot Area</div>
                      <div className="text-1 text-color-heading">
                        {convertedArea.toFixed(2)}
                      </div>
                    </div>
                    <select
                      value={selectedUnit}
                      onChange={(e) => setSelectedUnit(e.target.value)}
                      className="form-select text-2 form-control-sm w-auto"
                    >
                      <option value="sqft">sq.ft.</option>
                      <option value="sqyard">sq.yards</option>
                      <option value="sqm">sq.m.</option>
                      <option value="grounds">grounds</option>
                      <option value="aankadam">aankadam</option>
                      <option value="rood">rood</option>
                      <option value="chataks">chataks</option>
                      <option value="perch">perch</option>
                      <option value="guntha">guntha</option>
                      <option value="ares">ares</option>
                      <option value="biswa">biswa</option>
                      <option value="acre">acres</option>
                      <option value="bigha">bigha</option>
                      <option value="kottah">kottah</option>
                      <option value="hectare">hectares</option>
                      <option value="marla">marla</option>
                      <option value="kanal">kanal</option>
                      <option value="cent">cents</option>
                    </select>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec2">
                      <i className="fa fa-border-style"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">Zone</div>
                      <div className="text-1 text-color-heading">
                        {land.ln_zone}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec2">
                      <i className="fa fa-compass"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">Facing</div>
                      <div className="text-1 text-color-heading">
                        {land.ln_facing}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec3">
                      <i className="fa fa-th-large"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">
                        Boundary Wall
                      </div>
                      <div className="text-1 text-color-heading">
                        {land.ln_boundarywall ?? "--"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec4">
                      <i className="fa fa-star"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">
                        Featured plot
                      </div>
                      <div className="text-1 text-color-heading">
                        {land.ln_featured === "Yes" ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec5">
                      <i className="fa fa-user"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">
                        Seller Type
                      </div>
                      <div className="text-1 text-color-heading">
                        {land.ln_seller_type.charAt(0).toUpperCase() +
                          land.ln_seller_type.slice(1)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec1">
                      <i className="fa fa-key"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">
                        Possession Status
                      </div>
                      <div className="text-1 text-color-heading">
                        {land.ln_possesion_status ?? "--"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 pb-5">
                  <div className="box-icon">
                    <div className="icons sec2">
                      <i className="fa fa-crop"></i>
                    </div>
                    <div className="content">
                      <div className="text-4 text-color-default">
                        Corner Plot
                      </div>
                      <div className="text-1 text-color-heading">
                        {land.ln_corner}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="col-lg-12 pb-5" ref={contactSectionRef}>
            <div className="wg-property box-amenities spacing-3 mb-3 pb-4">
              <div className="row">
                <div className="col-sm-5 mb-2 mb-md-0 pb-5">
                  <div className="wg-title text-10 fw-6 text-color-heading pb-3 mb-4">
                    {land.us_usertype === 1 ? "Owner" : "Agent"} Details
                  </div>
                  <div className="row">
                    <div className="col-sm-3 col-4 mb-2 mb-md-0">
                      <img
                        src={
                          land.us_profileimage
                            ? `${API_BASE}/Uploads/profiles/${land.us_profileimage}`
                            : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/user.jpg`
                        }
                        alt={`${land.us_name}'s profile`}
                        className="img-fluid"
                      />
                    </div>
                    <div className="col-sm-9 ps-md-0 pt-4">
                      <div className="text-start">
                        <h6 className="plot-location-text mb-2">
                          <i className="bi bi-person me-2 text-16"></i>
                          {land.us_name ? land.us_name : "Owner Name"}
                        </h6>
                        <p className="ms-1 text-4 mb-2">
                          <i className="bi bi-telephone me-2"></i>
                          {isLoggedIn && hasEnquiry
                            ? `+91 ${land.us_mobile}`
                            : `+91 ${maskPhone(land.us_mobile)}`}
                        </p>
                        <p className="ms-1 text-4">
                          <i className="bi bi-envelope-at me-2"></i>
                          {isLoggedIn && hasEnquiry
                            ? land.us_email
                            : maskEmail(land.us_email)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {isLoggedIn && hasEnquiry ? (
                  <div className="col-sm-7 mb-2 mb-md-0">
                    <div className="box-overview">
                      <div className="row">
                        <div className="col-lg-12 pb-3">
                          <div className="box-icon">
                            <div className="icons">
                              <i className="fa fa-check-circle"></i>
                            </div>
                            <div className="content">
                              <div className="wg-title text-1 fw-6 text-color-heading mb-2">
                                Verified Listings Only
                              </div>
                              <p>
                                100% authentic land records and seller
                                verification.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-12 pb-3">
                          <div className="box-icon">
                            <div className="icons">
                              <i className="fa fa-search"></i>
                            </div>
                            <div className="content">
                              <div className="wg-title text-1 fw-6 text-color-heading mb-2">
                                Easy Search & Filters
                              </div>
                              <p>
                                Find plots by location, size, price, and
                                category.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-12 pb-3">
                          <div className="box-icon">
                            <div className="icons">
                              <i className="fa fa-phone"></i>
                            </div>
                            <div className="content">
                              {land.us_usertype === 2 ? (
                                <>
                                  <div className="wg-title text-1 fw-6 text-color-heading mb-2">
                                    Instant Buyer Leads - Direct inquiries on
                                    your listing.
                                  </div>
                                  <p>
                                    High Visibility - Targeted reach to genuine
                                    buyers.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <div className="wg-title text-1 fw-6 text-color-heading mb-2">
                                    Direct Owner Connect
                                  </div>
                                  <p>No middlemen, save brokerage.</p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-12 pb-3">
                          <div className="box-icon">
                            <div className="icons">
                              <i className="fa fa-list-ul"></i>
                            </div>
                            <div className="content">
                              <div className="wg-title text-1 fw-6 text-color-heading mb-2">
                                Free Property Listing
                              </div>
                              <p>
                                Post your land and reach thousands of buyers.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="col-sm-7 mb-2 mb-md-0">
                    <div className="wg-title text-10 fw-6 text-color-heading pb-3 mb-4">
                      Send Enquiry to{" "}
                      {land.us_usertype === 1 ? "Owner" : "Agent"}
                    </div>
                    <div className="row form">
                      <div className="col-lg-12">
                        <div className="box-radio-check d-flex align-items-start">
                          <label className="me-3">You are</label>
                          <div className="d-flex justify-content-start">
                            <fieldset className="radio-item style-1 me-4">
                              <label>
                                <span className="text-1">Buyer</span>
                                <input
                                  type="radio"
                                  name="userType"
                                  value="Buyer"
                                  checked={enquiryForm.userType === "Buyer"}
                                  onChange={handleUserTypeChange}
                                />
                                <span className="btn-radio"></span>
                              </label>
                            </fieldset>
                            <fieldset className="radio-item style-1 me-4">
                              <label>
                                <span className="text-1">Dealer</span>
                                <input
                                  type="radio"
                                  name="userType"
                                  value="Dealer"
                                  checked={enquiryForm.userType === "Dealer"}
                                  onChange={handleUserTypeChange}
                                />
                                <span className="btn-radio"></span>
                              </label>
                            </fieldset>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="box-fieldset pb-3">
                          <input
                            type="text"
                            name="name"
                            placeholder="Name"
                            className="form-control"
                            value={enquiryForm.name}
                            onChange={handleInputChange}
                            disabled={!!loggedInUserId}
                          />
                        </div>
                        <div className="box-fieldset pb-3">
                          <input
                            type="text"
                            name="email"
                            placeholder="Email"
                            className="form-control"
                            value={enquiryForm.email}
                            onChange={handleInputChange}
                            disabled={!!loggedInUserId}
                          />
                        </div>
                        <div className="box-fieldset pb-3">
                          <input
                            type="tel"
                            name="mobile"
                            placeholder="Mobile"
                            className="form-control"
                            value={enquiryForm.mobile}
                            onChange={handleInputChange}
                            disabled={!!loggedInUserId}
                          />
                        </div>
                      </div>

                      <div className="col-lg-6 pb-3">
                        <textarea
                          name="message"
                          className="textarea w-100 owner"
                          placeholder="Message"
                          value={enquiryForm.message}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>

                      <div className="box">
                        <a
                          href="#"
                          className="tf-btn bg-color-primary pd-20"
                          onClick={handleEnquirySubmit}
                        >
                          Contact {land.us_usertype === 1 ? "Owner" : "Agent"}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* OTP Verification Modal */}
            {showOtpModal && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                tabIndex={-1}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header p-4">
                      <h5 className="modal-title">Verify Your Account</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowOtpModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body p-4">
                      <p style={{ fontSize: "18px", lineHeight: "25px" }}>
                        An OTP has been sent to your registered email address.
                        Please enter it below to verify your account.
                      </p>
                      <form onSubmit={handleOtpSubmit}>
                        <div className="mb-3 mt-4 pt-3">
                          <div className="d-flex justify-content-between">
                            {otpDigits.map((digit, index) => (
                              <input
                                key={index}
                                id={`otp-digit-${index}`}
                                type="text"
                                className={`form-control text-center mx-2 py-3 ${
                                  otpError ? "is-invalid" : ""
                                }`}
                                value={digit}
                                style={{ fontSize: "16px" }}
                                maxLength={1}
                                onChange={(e) =>
                                  handleOtpDigitChange(index, e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Backspace" &&
                                    !digit &&
                                    index > 0
                                  ) {
                                    const prevInput = document.getElementById(
                                      `otp-digit-${index - 1}`
                                    );
                                    if (prevInput) prevInput.focus();
                                  }
                                }}
                              />
                            ))}
                          </div>
                          {otpError && (
                            <div className="invalid-feedback">{otpError}</div>
                          )}
                        </div>
                        <div className="d-grid">
                          <button
                            type="submit"
                            className="btn tf-btn bg-color-primary pd-8 w-100"
                            disabled={otpLoading}
                          >
                            {otpLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ms-2">Verifying...</span>
                              </>
                            ) : (
                              "Verify OTP"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Login Modal */}
            {showLoginModal && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                tabIndex={-1}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header p-4">
                      <h5 className="modal-title">Login to Continue</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => {
                          setShowLoginModal(false);
                          setLoginError("");
                        }}
                      ></button>
                    </div>
                    <div className="modal-body p-4">
                      <form onSubmit={handleLoginSubmit}>
                        <div className="mb-3">
                          <label
                            htmlFor="loginEmail"
                            className="form-label fw-bold"
                          >
                            Email:
                          </label>
                          <input
                            type="email"
                            className="form-control form-control-login"
                            id="loginEmail"
                            value={loginForm.email}
                            placeholder="Enter your email"
                            onChange={(e) =>
                              setLoginForm({
                                ...loginForm,
                                email: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label
                            htmlFor="loginPassword"
                            className="form-label fw-bold"
                          >
                            Password:
                          </label>
                          <input
                            type="password"
                            className="form-control form-control-login"
                            id="loginPassword"
                            value={loginForm.password}
                            placeholder="Enter your password"
                            onChange={(e) =>
                              setLoginForm({
                                ...loginForm,
                                password: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        {loginError && (
                          <div className="alert alert-danger">{loginError}</div>
                        )}
                        <div className="d-grid">
                          <button
                            type="submit"
                            className="btn tf-btn bg-color-primary pd-8 w-100"
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ms-2">Logging in...</span>
                              </>
                            ) : (
                              "Login"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="wg-property box-property-detail spacing-1 mb-3">
              <div className="wg-title text-1 fw-6 text-color-heading mb-3">
                Plot Description
              </div>
              <div className="content">
                <p className="description text-9 mb-10">{land.ln_desc}</p>
              </div>

              {/* Property Details */}
              <div className="box">
                <ul>
                  <li>
                    <p className="fw-6">Transaction Type (Resale)</p>
                    <p>
                      {land.ln_transaction_type.charAt(0).toUpperCase() +
                        land.ln_transaction_type.slice(1)}
                    </p>
                  </li>
                  <li>
                    <p className="fw-6">Width of Road Facing</p>
                    <p>{land.ln_width_roadfacing} ft</p>
                  </li>
                  <li>
                    <p className="fw-6">Overlooking</p>
                    <p>
                      {land.ln_overlooking
                        ? land.ln_overlooking.charAt(0).toUpperCase() +
                          land.ln_overlooking.slice(1)
                        : "--"}
                    </p>
                  </li>
                  <li>
                    <p className="fw-6">No. of Floors Allowed</p>
                    <p>{land.ln_allowed_floors || "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Fencing Type</p>
                    <p>{land.ln_fencing_type ?? "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Classification</p>
                    <p>{land.ln_classification}</p>
                  </li>
                  <li>
                    <p className="fw-6">Nearby Landmark</p>
                    <p>
                      {land.ln_nearby_landmark.charAt(0).toUpperCase() +
                        land.ln_nearby_landmark.slice(1)}
                    </p>
                  </li>
                </ul>

                <ul>
                  <li>
                    <p className="fw-6">Gated Society</p>
                    <p>{land.ln_gated}</p>
                  </li>
                  <li>
                    <p className="fw-6">
                      Electricity & Water Supply Availability
                    </p>
                    <p>
                      {land.ln_electric_supply} & {land.ln_water_supply}
                    </p>
                  </li>
                  <li>
                    <p className="fw-6">Topography</p>
                    <p>{land.ln_topography ?? "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Flood Zone Risk / Water Logging</p>
                    <p>{land.ln_water_looging ?? "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Authority Approved</p>
                    <p>{land.ln_authority_approve}</p>
                  </li>
                  <li>
                    <p className="fw-6">Mutation Done</p>
                    <p>{land.ln_mutation}</p>
                  </li>
                </ul>

                <ul>
                  <li>
                    <p className="fw-6">Patta Available</p>
                    <p>{land.ln_patta_avail ?? "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Soil Type</p>
                    <p>
                      {land.ln_soiltype
                        ? land.ln_soiltype.charAt(0).toUpperCase() +
                          land.ln_soiltype.slice(1)
                        : "--"}
                    </p>
                  </li>
                  <li>
                    <p className="fw-6">Status</p>
                    <p>{land.ln_legal_status}</p>
                  </li>
                  <li>
                    <p className="fw-6">Loan Available</p>
                    <p>{land.ln_loan ?? "--"}</p>
                  </li>
                  <li>
                    <p className="fw-6">Land Availability</p>
                    <p>
                      {land.ln_land_avail === "Avail"
                        ? "Available"
                        : "Sold out"}
                    </p>
                  </li>
                  <li>
                    <p className="fw-6">Site Visit Available</p>
                    <p>{land.ln_sitevisit_avail}</p>
                  </li>
                  {land.ln_sitevisit_link && (
                    <li>
                      <p className="fw-6">Site Visit Link</p>
                      <p>
                        <a
                          href={land.ln_sitevisit_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          gmap
                        </a>
                      </p>
                    </li>
                  )}
                  <li>
                    <p className="fw-6">Address</p>
                    <p>
                      <a href="#">{land.ln_address}</a>
                    </p>
                  </li>
                </ul>
              </div>

              {/* Plot Dimensions */}
              <div className="wg-title text-1 fw-6 text-color-heading mb-3 mt-5">
                Plot Dimensions in Feet
              </div>
              <div className="box">
                <div className="row">
                  <div className="col-6 col-md-3">
                    <p className="fw-6">East</p>
                    <p>{land.ln_dimension_east}</p>
                  </div>
                  <div className="col-6 col-md-3">
                    <p className="fw-6">West</p>
                    <p>{land.ln_dimension_west}</p>
                  </div>
                  <div className="col-6 col-md-3">
                    <p className="fw-6">North</p>
                    <p>{land.ln_dimension_north}</p>
                  </div>
                  <div className="col-6 col-md-3">
                    <p className="fw-6">South</p>
                    <p>{land.ln_dimension_south}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Growth Potentials */}
            <div className="wg-property box-property-detail spacing-1 pb-5 mb-3">
              <div className="wg-title text-1 fw-6 text-color-heading mb-3">
                Growth Potentials (upcoming features for that plot/land)
              </div>
              <div className="content mb-0">
                <p className="description text-9 mb-10">
                  {land.ln_growth ? land.ln_growth : "--"}
                </p>
              </div>
            </div>

            {/* Highlights */}
            <div className="wg-property box-amenities spacing-3">
              <div className="wg-title text-1 fw-6 text-color-heading mb-4">
                Highlights
              </div>
              <div className="wrap-feature">
                <div className="box-feature">
                  <div className="description text-9">
                    {land.ln_hilights ? land.ln_hilights : "--"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Lands and Recently Viewed */}
      <div className="tf-container py-5">
        <div className="row">
          {similarLands.length > 0 && (
            <div className="col-lg-8 pb-5">
              <div className="heading-section mb-46 text-center">
                <h2 className="title split-text effect-right">Similar Lands</h2>
              </div>
              <div className="d-none d-sm-block">
                <div
                  id="multiCarouselSimilarLands"
                  className="carousel slide mb-5"
                  data-bs-ride="carousel"
                >
                  <div className="carousel-inner">
                    {(() => {
                      const groupedLands = [];
                      for (let i = 0; i < similarLands.length; i += 3) {
                        groupedLands.push(similarLands.slice(i, i + 3));
                      }
                      return groupedLands.map((group, groupIndex) => (
                        <div
                          className={`carousel-item ${
                            groupIndex === 0 ? "active" : ""
                          }`}
                          key={groupIndex}
                        >
                          <ul className="carousel-list">
                            {group.map((similarLand) => (
                              <li key={similarLand.ln_id}>
                                <div className="box-location hover-img">
                                  <div className="plot-location">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    <div className="plot-location-text ms-2">
                                      {similarLand.ln_locality},<br />
                                      {similarLand.ln_address.split(",")[0]}
                                    </div>
                                  </div>
                                  <div className="plot-details sec">
                                    <div className="plot-details-row text-2">
                                      {similarLand.ln_total_sqft_area} sqft
                                      <br />
                                      Plot Area
                                    </div>
                                    <div className="plot-details-row text-2">
                                      {similarLand.ln_plottype}
                                    </div>
                                    <div className="plot-details-row text-2 no-border">
                                      {formatDate(similarLand.ln_post_date)}
                                      <br />
                                      Posted on
                                    </div>
                                  </div>
                                  <div className="image-wrap">
                                    <Link to={`/${similarLand.ln_seo_url}`}>
                                      <img
                                        className="lazyload"
                                        src={
                                          similarLand.ln_image &&
                                          similarLand.ln_image.length > 0
                                            ? `${API_BASE}/Uploads/lands/${similarLand.ln_image[0]}`
                                            : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                        }
                                        alt="Similar Land"
                                      />
                                    </Link>
                                  </div>
                                  <div className="content">
                                    <h4 className="text_white">
                                      <i className="bi bi-currency-rupee me-1"></i>
                                      {formatShortPrice(similarLand.ln_price)}
                                      <br />
                                      <small>
                                        <i className="bi bi-currency-rupee me-1"></i>
                                        {formatNumberIndian(
                                          similarLand.ln_total_area_sqft
                                        )}{" "}
                                        per sqft
                                      </small>
                                    </h4>
                                  </div>
                                  <Link
                                    to={`/${similarLand.ln_seo_url}`}
                                    className="click"
                                  >
                                    <i className="bi bi-arrow-up-right-circle-fill"></i>
                                  </Link>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="carousel-controls-center">
                    <button
                      className="carousel-control-prev swiper-button-next sw-button"
                      type="button"
                      data-bs-target="#multiCarouselSimilarLands"
                      data-bs-slide="prev"
                    >
                      <i className="icon-arrow-left-3"></i>
                    </button>
                    <button
                      className="carousel-control-next swiper-button-next sw-button"
                      type="button"
                      data-bs-target="#multiCarouselSimilarLands"
                      data-bs-slide="next"
                    >
                      <i className="icon-arrow-right-3"></i>
                    </button>
                  </div>
                </div>
              </div>
              {/* Mobile View */}
              <div className="mob-s d-block d-sm-none">
                <div className="container py-5">
                  <div
                    id="singleCardCarouselSimilarLands"
                    className="carousel slide"
                    data-bs-ride="carousel"
                  >
                    <div className="carousel-inner">
                      {similarLands.map((similarLand, index) => (
                        <div
                          className={`carousel-item ${
                            index === 0 ? "active" : ""
                          }`}
                          key={similarLand.ln_id}
                        >
                          <div className="box-location hover-img">
                            <div className="plot-location">
                              <i className="bi bi-geo-alt me-1"></i>
                              <div className="plot-location-text ms-2">
                                {similarLand.ln_locality},<br />
                                {similarLand.ln_address.split(",")[0]}
                              </div>
                            </div>
                            <div className="plot-details">
                              <div className="plot-details-row">
                                {similarLand.ln_total_sqft_area} sqft
                                <br />
                                Plot Area
                              </div>
                              <div className="plot-details-row">
                                {similarLand.ln_plottype}
                              </div>
                              <div className="plot-details-row no-border">
                                {formatDate(similarLand.ln_post_date)}
                                <br />
                                Posted on
                              </div>
                            </div>
                            <div className="image-wrap">
                              <Link to={`/${similarLand.ln_seo_url}`}>
                                <img
                                  className="lazyload"
                                  src={
                                    similarLand.ln_image &&
                                    similarLand.ln_image.length > 0
                                      ? `${API_BASE}/Uploads/lands/${similarLand.ln_image[0]}`
                                      : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                  }
                                  alt={similarLand.ln_title || "Similar Land"}
                                />
                              </Link>
                            </div>
                            <div className="content">
                              <h4 className="text_white">
                                <i className="bi bi-currency-rupee me-1"></i>
                                {formatShortPrice(similarLand.ln_price)}
                                <br />
                                <small>
                                  <i className="bi bi-currency-rupee me-1"></i>
                                  {formatNumberIndian(
                                    similarLand.ln_total_area_sqft
                                  )}{" "}
                                  per sqft
                                </small>
                              </h4>
                            </div>
                            <Link
                              to={`/${similarLand.ln_seo_url}`}
                              className="click"
                            >
                              <i className="bi bi-arrow-up-right-circle-fill"></i>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="carousel-bottom-controls">
                      <button
                        className="btn sw-button"
                        type="button"
                        data-bs-target="#singleCardCarouselSimilarLands"
                        data-bs-slide="prev"
                      >
                        <i className="icon-arrow-left-3"></i>
                      </button>
                      <button
                        className="btn sw-button"
                        type="button"
                        data-bs-target="#singleCardCarouselSimilarLands"
                        data-bs-slide="next"
                      >
                        <i className="icon-arrow-right-3"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {recentlyViewed && recentlyViewed.length > 0 && (
            <div className="col-lg-4 pb-5">
              <div className="tf-sidebar">
                <div className="sidebar-item sidebar-featured style-2 pb-36 mb-28">
                  <h5 className="sidebar-title mb-28">Recently Viewed Plots</h5>
                  <ul>
                    {recentlyViewed.slice(0, 3).map((recentLand) => (
                      <li
                        key={recentLand.ln_id}
                        className="box-listings style-2 hover-img"
                      >
                        <div className="image-wrap">
                          <img
                            className="ls-is-cached lazyloaded"
                            data-src={
                              recentLand.ln_image &&
                              recentLand.ln_image.length > 0
                                ? `${API_BASE}/Uploads/lands/${recentLand.ln_image[0]}`
                                : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                            }
                            src={
                              recentLand.ln_image &&
                              recentLand.ln_image.length > 0
                                ? `${API_BASE}/Uploads/lands/${recentLand.ln_image[0]}`
                                : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                            }
                            alt="Recently viewed land"
                          />
                        </div>
                        <div className="content">
                          <div className="text-4 title fw-5 lh-20">
                            <Link to={`/${recentLand.ln_seo_url}`}>
                              {recentLand.ln_plottype} Land at{" "}
                              {recentLand.ln_locality}
                            </Link>
                          </div>
                          <ul className="meta-list flex mb-1">
                            <li className="flex">
                              <small className="text-2">
                                <i className="bi bi-currency-rupee me-1"></i>
                                {formatNumberIndian(
                                  recentLand.ln_total_area_sqft
                                )}{" "}
                                per sqft
                              </small>
                            </li>
                          </ul>
                          <div className="price text-1 lh-20 fw-6">
                            <i className="bi bi-currency-rupee me-1"></i>
                            {formatShortPrice(recentLand.ln_price)} <br />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </FrontendLayout>
  );
};

export default PublicLandView;
